import omit from "lodash/omit";
import { Id } from "PFTypes";
import { StateCreator } from "zustand";

import {
  BookingEditModeStore,
  EditingCell,
  PendingEdit,
  PendingEditStatus,
  SmartPlannerModeActions,
  SmartPlannerModeSlice
} from "../booking_edit_mode_store.types";
import { INITIAL_SMART_PLANNER_MODE_STATE } from "../constants";

export const createSmartPlannerSlice: StateCreator<
  BookingEditModeStore,
  [],
  [],
  { smartPlannerMode: SmartPlannerModeSlice }
> = () => ({
  smartPlannerMode: INITIAL_SMART_PLANNER_MODE_STATE
});

export const createSmartPlannerModeActions = (
  set: (fn: (state: BookingEditModeStore) => void | Partial<BookingEditModeStore>) => void
): SmartPlannerModeActions => ({
  setEditingCell: (cell: EditingCell | null) => {
    set((state) => {
      state.smartPlannerMode.editingCell = cell;
    });
  },

  clearEditingCell: () => {
    set((state) => {
      state.smartPlannerMode.editingCell = null;
    });
  },

  setUpdatableBookingIds: (ids: Id[]) => {
    set((state) => {
      state.smartPlannerMode.updatableBookingIds = ids;
    });
  },

  addUpdatableBookingIds: (ids: Id[]) => {
    set((state) => {
      const current = state.smartPlannerMode.updatableBookingIds;
      const newIds = ids.filter((id) => !current.includes(id));
      if (newIds.length > 0) {
        state.smartPlannerMode.updatableBookingIds = [...current, ...newIds];
      }
    });
  },

  removeUpdatableBookingIds: (ids: Id[]) => {
    set((state) => {
      const idsToRemove = new Set(ids);
      state.smartPlannerMode.updatableBookingIds = state.smartPlannerMode.updatableBookingIds.filter(
        (id) => !idsToRemove.has(id)
      );
    });
  },

  setIsLoading: (value: boolean) => {
    set((state) => {
      state.smartPlannerMode.isLoading = value;
    });
  },

  commitEdit: (key: string, edit: Omit<PendingEdit, "status">) => {
    set((state) => {
      state.smartPlannerMode.pendingEdits[key] = {
        ...edit,
        status: PendingEditStatus.Pending
      };
    });
  },

  markInflight: (key: string) => {
    set((state) => {
      const edit = state.smartPlannerMode.pendingEdits[key];
      if (edit) {
        edit.status = PendingEditStatus.Inflight;
      }
    });
  },

  clearEdit: (keys: string | string[]) => {
    set((state) => {
      state.smartPlannerMode.pendingEdits = omit(state.smartPlannerMode.pendingEdits, keys);
    });
  },

  revertEdit: (key: string) => {
    set((state) => {
      delete state.smartPlannerMode.pendingEdits[key];
    });
  },

  resetPendingEdits: () => {
    set((state) => {
      state.smartPlannerMode.pendingEdits = {};
    });
  },

  reset: () => {
    set((state) => {
      state.smartPlannerMode = INITIAL_SMART_PLANNER_MODE_STATE;
    });
  }
});
